<template>
  <div>
    <Head title="Categorías de Gastos" />

    <!-- Top Header -->
    <div class="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">
          <Link class="text-indigo-400 hover:text-indigo-600" href="/spents">Gastos</Link>
          <span class="text-indigo-400 font-medium">/</span> Categorías
        </h1>
        <p class="mt-1 text-sm text-gray-500">
          Administración y configuración del maestro de categorías para los gastos del condominio.
        </p>
      </div>
      <div class="mt-4 flex md:mt-0 space-x-3">
        <Link
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          href="/spents"
        >
          <svg class="-ml-1 mr-2 h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver a Gastos
        </Link>
        <Link
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          href="/spent-categories/create"
        >
          <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Nueva Categoría
        </Link>
      </div>
    </div>

    <!-- Search bar -->
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset" />
    </div>

    <!-- Categories Table -->
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold bg-gray-50 text-gray-600 text-sm">
            <th class="pb-4 pt-6 px-6">Nombre de Categoría</th>
            <th class="pb-4 pt-6 px-6 text-center">Gastos Asociados</th>
            <th class="pb-4 pt-6 px-6 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="category in categories.data"
            :key="category.id"
            class="hover:bg-gray-50 focus-within:bg-gray-50 transition-colors"
          >
            <td class="border-t px-6 py-4">
              <Link
                class="flex items-center font-medium text-gray-900 hover:text-indigo-600"
                :href="`/spent-categories/${category.id}/edit`"
              >
                {{ category.name }}
              </Link>
            </td>
            <td class="border-t px-6 py-4 text-center">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="category.spents_count > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'"
              >
                {{ category.spents_count }} {{ category.spents_count === 1 ? 'gasto' : 'gastos' }}
              </span>
            </td>
            <td class="border-t px-6 py-4 text-right space-x-2">
              <Link
                class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                :href="`/spent-categories/${category.id}/edit`"
              >
                <icon name="edit" class="w-4 h-4 mr-1 fill-indigo-700" />
                Editar
              </Link>
              <button
                type="button"
                class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                @click="destroy(category)"
              >
                <icon name="trash" class="w-4 h-4 mr-1 fill-red-700" />
                Eliminar
              </button>
            </td>
          </tr>
          <tr v-if="categories.data.length === 0">
            <td class="px-6 py-8 border-t text-center text-gray-500" colspan="3">
              No se encontraron categorías de gastos registradas.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <pagination class="mt-6" :links="categories.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'

export default {
  components: {
    Head,
    Icon,
    Link,
    Pagination,
    SearchFilter,
  },
  layout: Layout,
  props: {
    filters: Object,
    categories: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/spent-categories', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    destroy(category) {
      if (category.spents_count > 0) {
        alert(`No se puede eliminar la categoría "${category.name}" porque tiene ${category.spents_count} gasto(s) asociado(s).`)
        return
      }
      if (confirm(`¿Estás seguro de que deseas eliminar la categoría "${category.name}"?`)) {
        this.$inertia.delete(`/spent-categories/${category.id}`)
      }
    },
  },
}
</script>
